import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Input, Row, Space, Switch, Tag, Typography, Divider, message } from "antd";
import { useNavigate } from "react-router-dom";

import {
    GetV2EventCatalog,
    GetV2Automation,
    CreateV2Automation,
    UpdateV2Automation,
    GetV2AutomationTrackers,
} from "../../api/v2/automations";

import "./automation-editor.css";
import CopyToClipboardCode from "../../components/copyToClipboardCode/CopyToClipboardCode";
import TriggersEditor from "./TriggersEditor";
import StepsEditor from "./StepsEditor";
import RunsViewer from "./RunsViewer";
import Loader from "../../components/loader/Loader";


const { Title, Text } = Typography;

type UiStatus = "draft" | "active" | "paused";

export type V2AutomationForm = {
    id?: number;
    name: string;
    description: string | null;
    active: boolean;
};

type EventCatalogDto = {
    existing_events: string[];
    suggested_events: string[];
};

type TrackersDto = {
    events: Array<{
        event_name: string;
        required: boolean;
        description?: string;
        example_code?: string;
    }>;
    integration_snippet?: string;
};

type Props = {
    domain: string;
    automationId?: number;
    onSaved?: (automationId: number) => void;
};

function boolToActive(v: boolean): 0 | 1 {
    return v ? 1 : 0;
}

function activeToBool(active: 0 | 1): boolean {
    return active === 1;
}

export default function AutomationEditor(props: Props) {
    const { domain, automationId, onSaved } = props;
    const navigate = useNavigate();

    const [form] = Form.useForm<V2AutomationForm>();
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    const [eventCatalog, setEventCatalog] = useState<EventCatalogDto>({ existing_events: [], suggested_events: [] });
    const [trackers, setTrackers] = useState<TrackersDto | null>(null);

    const isEdit = !!automationId;

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);

            const [catalogRes, automationRes] = await Promise.all([
                GetV2EventCatalog(domain),
                isEdit ? GetV2Automation(domain, automationId!) : Promise.resolve(null),
            ]);

            if (cancelled) return;

            if (!catalogRes?.ok) {
                message.error(catalogRes?.error || "Failed loading event catalog");
            } else {
                setEventCatalog(catalogRes.data!);
            }

            if (isEdit) {
                if (!automationRes || !automationRes.ok) {
                    message.error((automationRes as any)?.error || "Failed loading automation");
                } else {
                    const a = automationRes.data!;
                    form.setFieldsValue({
                        id: a.id,
                        name: a.name,
                        description: a.description ?? null,
                        active: activeToBool(a.active),
                    });

                    const trackersRes = await GetV2AutomationTrackers(domain, automationId!);
                    if (trackersRes.ok) setTrackers(trackersRes.data!);
                    else setTrackers(null);
                }
            } else {
                form.setFieldsValue({
                    name: "",
                    description: null,
                    active: false,
                });
                setTrackers(null);
            }

            setLoading(false);
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [domain, automationId, isEdit, form]);

    const refreshTrackers = async (idToUse: number) => {
        const trackersRes = await GetV2AutomationTrackers(domain, idToUse);

        if (trackersRes.ok) {
            setTrackers(trackersRes.data || { events: [], integration_snippet: "" });
        } else {
            message.error(trackersRes.error || "Failed loading trackers");
            setTrackers({ events: [], integration_snippet: "" });
        }
    };

    const onSave = async () => {
        const values = await form.validateFields();

        setSaving(true);
        try {
            const body = {
                name: values.name,
                description: values.description || null,
                active: boolToActive(values.active),
            } as const;

            if (!isEdit) {
                const createRes = await CreateV2Automation(domain, body);

                if (!createRes.ok) {
                    message.error(createRes.error || "Failed creating automation");
                    return;
                }

                message.success("Automation created");
                const newId = createRes.data!.id;

                const trackersRes = await GetV2AutomationTrackers(domain, newId);
                if (trackersRes.ok) setTrackers(trackersRes.data!);
                else setTrackers(null);

                onSaved?.(newId);
            } else {
                const updateRes = await UpdateV2Automation(domain, automationId!, body);

                if (!updateRes.ok) {
                    message.error(updateRes.error || "Failed updating automation");
                    return;
                }

                message.success("Automation saved");

                const trackersRes = await GetV2AutomationTrackers(domain, automationId!);
                if (trackersRes.ok) setTrackers(trackersRes.data!);
                else setTrackers(null);

                onSaved?.(automationId!);
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="v2ae-loading">
                <Loader size={72} isFullPage={false} />
            </div>
        );
    }

    return (
        <div className="v2ae-root">
            <Row gutter={16}>
                <Col span={16}>
                    <Card>
                        <Title className="v2ae-title">{isEdit ? "Edit Automation" : "Create Automation"}</Title>

                        <Form form={form} layout="vertical">
                            <Row gutter={16}>
                                <Col span={16}>
                                    <Form.Item name="name" label="Name" rules={[{ required: true, message: "Please enter a name" }]}>
                                        <Input placeholder="e.g. Abandoned cart recovery" />
                                    </Form.Item>
                                </Col>

                                <Col span={8}>
                                    <Form.Item
                                        name="active"
                                        label="Active"
                                        valuePropName="checked"
                                        tooltip="When inactive, triggers will not create new runs."
                                    >
                                        <Switch checkedChildren="On" unCheckedChildren="Off" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="description" label="Description (optional)">
                                <Input.TextArea rows={3} placeholder="Short description for internal use" />
                            </Form.Item>

                            <Divider />

                            <Title id="automation-triggers" className="v2ae-section-title">Triggers</Title>
                            <Text type="secondary">
                                Add one or more triggers. Each trigger has its own entry condition tree (event-based) and optional exit condition later.
                            </Text>

                            <div className="v2ae-spacer-16" />

                            {isEdit ? (
                                <TriggersEditor
                                    domain={domain}
                                    automationId={automationId!}
                                    onChanged={() => refreshTrackers(automationId!)}
                                    eventSuggestions={[
                                        ...(eventCatalog?.existing_events || []),
                                        ...(eventCatalog?.suggested_events || []),
                                    ]}
                                />
                            ) : (
                                <Text type="secondary">Create the automation first, then you can add triggers.</Text>
                            )}

                            <Divider />

                            <Title id="automation-steps" className="v2ae-section-title">Steps</Title>
                            <Text type="secondary">Define what happens after a trigger creates a run.</Text>

                            <div className="v2ae-spacer-16" />

                            {isEdit ? (
                                <StepsEditor domain={domain} automationId={automationId!} />
                            ) : (
                                <Text type="secondary">Create the automation first, then you can add steps.</Text>
                            )}

                            <Divider />

                            <Title id="automation-runs" className="v2ae-section-title">Runs & logs</Title>
                            <Text type="secondary">
                                View recent runs and their logs (run_created, step_skipped, send_attempt, errors, etc).
                            </Text>

                            <div className="v2ae-spacer-16" />

                            {isEdit ? (
                                <RunsViewer domain={domain} automationId={automationId!} />
                            ) : (
                                <Text type="secondary">Create the automation first, then runs will appear here.</Text>
                            )}

                            <Divider />

                            <Space>
                                <Button type="primary" onClick={onSave} loading={saving}>
                                    {isEdit ? "Save" : "Create"}
                                </Button>
                                {isEdit ? (
                                    <Button onClick={() => navigate(`/automations-pro/${automationId}/tree`)}>
                                        Open Tree Editor
                                    </Button>
                                ) : null}
                            </Space>
                        </Form>
                    </Card>
                </Col>

                <Col span={8}>
                    <Card>
                        <Title className="v2ae-section-title">Required trackers</Title>

                        {!trackers ? (
                            <Text type="secondary">Once you add triggers/conditions, we’ll show what events you must track on the website.</Text>
                        ) : (
                            <>
                                <div className="v2ae-tags">
                                    {trackers.events.map((e) => (
                                        <Tag key={e.event_name} color={e.required ? "blue" : "default"}>
                                            {e.event_name}
                                        </Tag>
                                    ))}
                                </div>

                                {trackers.events.map((e) => (
                                    <div key={e.event_name} className="v2ae-tracker-item">
                                        <Text>{e.event_name}</Text>
                                        {e.description ? (
                                            <div>
                                                <Text type="secondary">{e.description}</Text>
                                            </div>
                                        ) : null}

                                        {e.example_code ? <CopyToClipboardCode content={e.example_code} /> : null}
                                    </div>
                                ))}

                                {trackers.integration_snippet ? (
                                    <>
                                        <Divider />
                                        <Text>Generic snippet</Text>
                                        <pre className="v2ae-pre">{trackers.integration_snippet}</pre>
                                    </>
                                ) : null}
                            </>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
